import { Box, CircularProgress } from "@mui/material";
import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import ConfigurableValues from "./config/constants";
import Carts from "./screens/Carts/Carts";
import Checkout from "./screens/Checkout/Checkout";
import EmailSent from "./screens/EmailSent/EmailSent";
import Favourites from "./screens/Favourites/Favourites";
import ForgotPassword from "./screens/ForgotPassword/ForgotPassword";
import Home from "./screens/Home/Home";
import Login from "./screens/Login/Login";
import LoginEmail from "./screens/LoginEmail/LoginEmail";
import MyOrders from "./screens/MyOrders/MyOrders";
import NewLogin from "./screens/NewLogin/NewLogin";
import OrderDetail from "./screens/OrderDetail/OrderDetail";
import Paypal from "./screens/Paypal/Paypal";
import Privacy from "./screens/Privacy/Privacy";
import Profile from "./screens/Profile/Profile";
import Settings from "./screens/Settings/Settings";
import Registration from "./screens/Registration/Registration";
import PhoneNumber from "./screens/PhoneNumber/PhoneNumber";
import VerifyEmail from "./screens/VerifyEmail/VerifyEmail";
import VerifyForgotOtp from "./screens/VerifyForgotOtp/VerifyForgotOtp";
import Reactivate from "./screens/Reactivate/Reactivate";
import ResetPassword from "./screens/ResetPassword/ResetPassword";
import RestaurantDetail from "./screens/RestaurantDetail/RestaurantDetail";
import Restaurants from "./screens/Restaurants/Restaurants";
import Catering from "./screens/Catering/Catering";
import Stripe from "./screens/Stripe/Stripe";
import NMI from "./screens/NMI/NMI";
import Terms from "./screens/Terms/Terms";
import Support from "./screens/Support/Support";
import DeleteAccount from "./screens/DeleteAccount/DeleteAccount";
import Deals from "./screens/Deals/Deals";
import Rewards from "./screens/Rewards/Rewards";
import Membership from "./screens/Membership/Membership";
import PaymentMethods from "./screens/PaymentMethods/PaymentMethods";
import MyCoupons from "./screens/MyCoupons/MyCoupons";
import PromotionalCoupons from "./screens/PromotionalCoupons/PromotionalCoupons";
import Wallet from "./screens/Wallet/Wallet";
import MyTickets from "./screens/MyTickets/MyTickets";
import TicketDetail from "./screens/TicketDetail/TicketDetail";
import GetHelp from "./screens/GetHelp/GetHelp";
import PartnerSignup from "./screens/PartnerSignup/PartnerSignup";
import MobileUpload from "./screens/PartnerSignup/MobileUpload";
import PayoutSetup from "./screens/PartnerSignup/PayoutSetup";
import Reserve from "./screens/Reserve/Reserve";
import MyReservations from "./screens/MyReservations/MyReservations";
import * as Sentry from "@sentry/react";
import AuthRoute from "./routes/AuthRoute";
import PrivateRoute from "./routes/PrivateRoute";
import EmbedGuard from "./routes/EmbedGuard";
import VerifyPhone from "./screens/VerifyPhone/VerifyPhone";
import ChangePhone from "./screens/ChangePhone/ChangePhone";
import ChangeEmail from "./screens/ChangeEmail/ChangeEmail";
import { SearchProvider } from "./context/Search";
import { EmbedProvider } from "./context/Embed";
import MainLayout from "./components/SideNav/MainLayout";
import DebugErrorBoundary from "./components/DebugErrorBoundary";
import PerfDebugPanel from "./components/PerfDebugPanel/PerfDebugPanel";
import InstallPrompt from "./components/InstallPrompt/InstallPrompt";
import AutoPushPrompt from "./components/AutoPushPrompt/AutoPushPrompt";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import { perfMark } from "./utils/perfDebug";
//import { fetchConfiguration } from "./utils/helper";

// Lazy so three.js stays out of the main bundle until /home-3d is visited
const Home3D = React.lazy(() => import("./screens/Home3D/Home3D"));


function App() {
  const { MAPBOX_TOKEN, SENTRY_DSN } = ConfigurableValues();

  useEffect(() => {
  
    if (SENTRY_DSN) {
      Sentry.init({
        dsn: SENTRY_DSN,
        environment: process.env.NODE_ENV || "production",

        // Errors only. This is the highest-traffic surface we have and the
        // Sentry plan is the free 5k events/month tier — tracing would spend
        // the whole budget on timing data instead of bugs.
        tracesSampleRate: 0,

        // Noise that is not our bug. On a public consumer site this is the
        // majority of what the browser reports: extension conflicts, users
        // navigating away mid-request, and the harmless ResizeObserver warning.
        // Left unfiltered it would drain the monthly quota and bury real errors.
        ignoreErrors: [
          "ResizeObserver loop limit exceeded",
          "ResizeObserver loop completed with undelivered notifications",
          "Non-Error promise rejection captured",
          "Failed to fetch",
          "NetworkError when attempting to fetch resource",
          "Load failed",
          "AbortError",
        ],
        denyUrls: [
          /extensions\//i,
          /^chrome:\/\//i,
          /^chrome-extension:\/\//i,
          /^moz-extension:\/\//i,
          /^safari-extension:\/\//i,
        ],

        // Same rule as the backend: never let checkout data reach a third
        // party. Addresses, phone numbers and card tokens live in these
        // requests, so strip bodies and auth headers on the way out.
        beforeSend(event) {
          if (event.request) {
            delete event.request.data;
            delete event.request.cookies;
            if (event.request.headers) {
              delete event.request.headers.Authorization;
              delete event.request.headers.Cookie;
            }
          }
          return event;
        },
      });
    }

  }, [SENTRY_DSN]);

  // Kept as a timing marker only. The app no longer waits for this: rendering
  // used to be gated on MAPBOX_TOKEN, which arrives with the Configuration
  // query, so every visitor stared at a spinner for that round trip and the
  // menu query could not even start until it finished. Maps now hold themselves
  // back individually via MapWhenReady.
  useEffect(() => {
    if (MAPBOX_TOKEN) perfMark("configuration loaded (no longer blocking)");
  }, [MAPBOX_TOKEN]);

  return (
    <>
      <PerfDebugPanel />
      <AutoPushPrompt />
    <EmbedProvider>
    <SearchProvider>
    {/* Real paths, not #/ routes: Google ignores everything after "#", so under
        the old HashRouter every page was "/" to it. Old #/ links are rewritten
        before React mounts (upgradeHashUrl in index.js), and a new top-level
        route must be added to APP_ROUTES there too. */}
    <BrowserRouter>
        <InstallPrompt />
        <ScrollToTop />
        <DebugErrorBoundary>
        <EmbedGuard>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/home-3d"
            element={
              <React.Suspense
                fallback={
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 12 }}>
                    <CircularProgress color="error" />
                  </Box>
                }
              >
                <Home3D />
              </React.Suspense>
            }
          />
          <Route path="/restaurant-list" element={<MainLayout><Restaurants /></MainLayout>} />
          <Route path="/catering" element={<MainLayout><Catering /></MainLayout>} />
          <Route path="/restaurant/:slug" element={<MainLayout hideSidebar><RestaurantDetail /></MainLayout>} />
          <Route path="/privacy" element={<MainLayout><Privacy /></MainLayout>} />
          <Route path="/terms" element={<MainLayout><Terms /></MainLayout>} />
          <Route path="/support" element={<MainLayout><Support /></MainLayout>} />
          <Route path="/delete-account" element={<MainLayout><DeleteAccount /></MainLayout>} />
          <Route path="/partner-signup" element={<PartnerSignup />} />
          <Route path="/partner-signup/upload" element={<MobileUpload />} />
          <Route path="/partner-signup/payouts" element={<PayoutSetup />} />
          {/* The same list as /restaurant-list, in pickup mode. */}
          <Route path="/pickup" element={<MainLayout><Restaurants pickupPage /></MainLayout>} />
          <Route path="/deals" element={<MainLayout><Deals /></MainLayout>} />
          <Route path="/reserve/:slug" element={<PrivateRoute><MainLayout><Reserve /></MainLayout></PrivateRoute>} />
          <Route path="/reservations" element={<PrivateRoute><MainLayout><MyReservations /></MainLayout></PrivateRoute>} />
          <Route
            path={"/login"}
            element={
              <AuthRoute>
                <Login />
              </AuthRoute>
            }
          />
          <Route
            path={"/registration"}
            element={
              <AuthRoute>
                <Registration />
              </AuthRoute>
            }
          />
          <Route
            path={"/new-login"}
            element={
              <AuthRoute>
                <NewLogin />
              </AuthRoute>
            }
          />
          <Route
            path={"/login-email"}
            element={
              <AuthRoute>
                <LoginEmail />
              </AuthRoute>
            }
          />
          <Route
            path={"/verify-email"}
            element={
              <AuthRoute>
                <VerifyEmail />
              </AuthRoute>
            }
          />
          <Route
            path={"/new-password"}
            element={
              <AuthRoute>
                <ResetPassword />
              </AuthRoute>
            }
          />
          <Route
            path={"/phone-number"}
            element={
              <PrivateRoute>
                <MainLayout><PhoneNumber /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/verify-phone"}
            element={
              <PrivateRoute>
                <MainLayout><VerifyPhone /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/change-phone"}
            element={
              <PrivateRoute>
                <MainLayout><ChangePhone /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/change-email"}
            element={
              <PrivateRoute>
                <MainLayout><ChangeEmail /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/forgot-password"}
            element={
              <AuthRoute>
                <ForgotPassword />
              </AuthRoute>
            }
          />
          <Route
            path={"/reactivate"}
            element={
              <AuthRoute>
                <Reactivate />
              </AuthRoute>
            }
          />
          <Route
            path={"/verify-forgot-otp"}
            element={
              <AuthRoute>
                <VerifyForgotOtp />
              </AuthRoute>
            }
          />
          <Route
            path={"/email-sent"}
            element={
              <AuthRoute>
                <EmailSent />
              </AuthRoute>
            }
          />
          <Route
            path={"/orders"}
            element={
              <PrivateRoute>
                <MainLayout><MyOrders /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/profile"}
            element={
              <PrivateRoute>
                <MainLayout><Profile /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/settings"}
            element={
              <PrivateRoute>
                <MainLayout><Settings /></MainLayout>
              </PrivateRoute>
            }
          />
          {/* PrivateRoute rather than a bare <Login />: it records where the
              customer was, so signing back in drops them straight back on
              checkout with their cart instead of on the home page. */}
          {/* The cart button lands here when there's more than one cart, so
              the customer picks which one instead of being dropped into
              whichever happened to be active. */}
          <Route
            path={"/carts"}
            element={
              <PrivateRoute>
                <Carts />
              </PrivateRoute>
            }
          />
          <Route
            path={"/checkout"}
            element={
              <PrivateRoute>
                {/* Checkout brings its own focused header and no sidebar. */}
                <MainLayout hideSidebar><Checkout /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/order-detail/:id"}
            element={
              <PrivateRoute>
                <MainLayout><OrderDetail /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/paypal"}
            element={
              <PrivateRoute>
                <MainLayout><Paypal /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/stripe"}
            element={
              <PrivateRoute>
                <MainLayout><Stripe /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/nmi"}
            element={
              <PrivateRoute>
                <MainLayout><NMI /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/favourite"}
            element={
              <PrivateRoute>
                <MainLayout><Favourites /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/rewards"}
            element={
              <PrivateRoute>
                <MainLayout><Rewards /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/membership"}
            element={
              <PrivateRoute>
                <MainLayout><Membership /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/payment-methods"}
            element={
              <PrivateRoute>
                <MainLayout><PaymentMethods /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/my-coupons"}
            element={
              <PrivateRoute>
                <MainLayout><MyCoupons /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/promotional-coupons"}
            element={
              <PrivateRoute>
                <MainLayout><PromotionalCoupons /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/wallet"}
            element={
              <PrivateRoute>
                <MainLayout><Wallet /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/my-tickets"}
            element={
              <PrivateRoute>
                <MainLayout><MyTickets /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/ticket-detail/:id"}
            element={
              <PrivateRoute>
                <MainLayout><TicketDetail /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/get-help/:orderId"}
            element={
              <PrivateRoute>
                <MainLayout><GetHelp /></MainLayout>
              </PrivateRoute>
            }
          />
          <Route
            path={"/get-help"}
            element={
              <PrivateRoute>
                <MainLayout><GetHelp /></MainLayout>
              </PrivateRoute>
            }
          />
          {/* Unknown paths (and short links that matched no restaurant). */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </EmbedGuard>
        </DebugErrorBoundary>
    </BrowserRouter>
    </SearchProvider>
    </EmbedProvider>
    </>
  );
}

export default Sentry.withProfiler(App);
